/**
 * BhStore — a drop-in replacement for `localStorage` used by the original
 * app's screens. Reads are instant (from memory). Writes are saved on this
 * device straight away and sent to the database shortly after, one row per
 * record, so two people editing different medicines never overwrite each
 * other.
 *
 * Merging: when a key is changed here, we remember the server rows it was
 * based on. On sync we only send rows that actually changed against that
 * base, so a change another device made to a different row is kept.
 */
import { DEVICE_KEYS, PEOPLE_KEY, isAppKey, keyFor, parseKey, tableOf } from './keys';
import type { Change, DataTable, FamilySnapshot, RemoteEvent, Role } from './types';
import { RejectedError, isSettingTable } from './types';

export interface StoreBackend {
  loadFamilyData(familyId: string): Promise<FamilySnapshot>;
  applyChange(familyId: string, change: Change): Promise<void>;
}

/** Minimal Storage used for the offline cache (real localStorage in the browser). */
export interface PersistStorage { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

export type SyncStatus = 'saved' | 'saving' | 'offline' | 'error';

interface RowVal { position: number; data: unknown }
type RowMap = Map<string, RowVal>; // rowId -> value

interface Persisted {
  v: 1;
  server: [string, [string, RowVal][]][];
  local: [string, string | null][];
  base: [string, [string, RowVal][]][];
  fetchedAt: number;
}

/** JSON with sorted object keys, so equal data always compares equal. */
export function stableStringify(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return '[' + v.map(x => (x === undefined ? 'null' : stableStringify(x))).join(',') + ']';
  const keys = Object.keys(v as object).filter(k => (v as Record<string, unknown>)[k] !== undefined).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify((v as Record<string, unknown>)[k])).join(',') + '}';
}
const sameRow = (a?: RowVal, b?: RowVal) => !!a && !!b && a.position === b.position && stableStringify(a.data) === stableStringify(b.data);

/** Turn the string the app saved into rows. Returns null if it isn't a list of records. */
export function toRows(table: DataTable, value: string | null): RowMap | null {
  const rows: RowMap = new Map();
  if (value === null) return rows;
  if (isSettingTable(table)) { rows.set('value', { position: 0, data: value }); return rows; }
  let arr: unknown;
  try { arr = JSON.parse(value); } catch { return null; }
  if (!Array.isArray(arr)) return null;
  arr.forEach((item, i) => {
    let id = item && typeof item === 'object' && (typeof (item as { id?: unknown }).id === 'string' || typeof (item as { id?: unknown }).id === 'number')
      ? String((item as { id: unknown }).id) : `__pos_${i}`;
    while (rows.has(id)) id = `${id}#dup`;
    rows.set(id, { position: i, data: item });
  });
  return rows;
}
function fromRows(table: DataTable, rows: RowMap | undefined): string | null {
  if (!rows || rows.size === 0) return null;
  if (isSettingTable(table)) return String(rows.get('value')?.data ?? '');
  const sorted = [...rows.entries()].sort((a, b) => a[1].position - b[1].position || (a[0] < b[0] ? -1 : 1));
  return JSON.stringify(sorted.map(([, r]) => r.data));
}

export interface StoreOptions {
  familyId: string;
  userId: string;
  role: Role;
  backend: StoreBackend;
  persist: PersistStorage;
  /** Called when data changed underneath the screens (another device, a rejected change). */
  onExternalChange?: () => void;
  onStatus?: (s: SyncStatus, pending: number) => void;
  onNotice?: (msg: string) => void;
  debounceMs?: number;
}

export class BhStore {
  private server = new Map<string, RowMap>();     // key -> rows as last seen on the server
  private local = new Map<string, string | null>(); // key -> value written here, not yet confirmed (null = removed)
  private base = new Map<string, RowMap>();        // key -> server rows when the local edit started
  private version = new Map<string, number>();
  private remoteTouched = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing: Promise<void> | null = null;
  private retryMs = 2000;
  private status: SyncStatus = 'saved';
  private fetchedAt = 0;
  role: Role;

  constructor(private o: StoreOptions) {
    this.role = o.role;
  }

  // ------------------------------------------------------------------
  // Loading
  // ------------------------------------------------------------------
  private get cacheKey() { return `bh.cache.v1.${this.o.userId}.${this.o.familyId}`; }
  private deviceKey(k: string) { return `bh.dev.${this.o.userId}.${this.o.familyId}.${k}`; }

  /** Load the offline copy saved on this device. Returns false if there is none. */
  loadCache(): boolean {
    const raw = this.o.persist.getItem(this.cacheKey);
    if (!raw) return false;
    try {
      const p = JSON.parse(raw) as Persisted;
      if (p.v !== 1) return false;
      this.server = new Map(p.server.map(([k, rows]) => [k, new Map(rows)]));
      this.local = new Map(p.local);
      this.base = new Map(p.base.map(([k, rows]) => [k, new Map(rows)]));
      this.fetchedAt = p.fetchedAt;
      if (this.local.size) this.scheduleFlush(0);
      return true;
    } catch { return false; }
  }

  get hasData() { return this.server.size > 0 || this.local.size > 0; }
  get lastFetchedAt() { return this.fetchedAt; }

  /** Replace the server view with a fresh download. Returns true if anything visible changed. */
  applySnapshot(snap: FamilySnapshot): boolean {
    const before = this.visibleFingerprint();
    const next = new Map<string, RowMap>();
    for (const r of snap.rows) {
      const key = isSettingTable(r.table) ? r.id : keyFor(r.table, r.patientId);
      const rowId = isSettingTable(r.table) ? 'value' : r.id;
      if (!next.has(key)) next.set(key, new Map());
      next.get(key)!.set(rowId, { position: r.position, data: r.data });
    }
    this.server = next;
    this.fetchedAt = snap.fetchedAt;
    for (const k of this.local.keys()) this.remoteTouched.add(k);
    this.save();
    return before !== this.visibleFingerprint();
  }

  async refresh(): Promise<boolean> {
    const snap = await this.o.backend.loadFamilyData(this.o.familyId);
    return this.applySnapshot(snap);
  }

  private visibleFingerprint(): string {
    const keys = this.keys().sort();
    return keys.map(k => k + '=' + this.getItem(k)).join('\n');
  }

  // ------------------------------------------------------------------
  // localStorage API
  // ------------------------------------------------------------------
  getItem(key: string): string | null {
    key = String(key);
    if (DEVICE_KEYS.has(key)) return this.o.persist.getItem(this.deviceKey(key));
    if (this.local.has(key)) return this.local.get(key)!;
    const spec = parseKey(key);
    const table = tableOf(spec)!;
    return fromRows(table, this.server.get(key));
  }

  setItem(key: string, value: string): void {
    key = String(key); value = String(value);
    if (DEVICE_KEYS.has(key)) { this.o.persist.setItem(this.deviceKey(key), value); return; }
    if (this.getItem(key) === value) return; // nothing changed
    if (!this.canWrite(key)) { this.refuse(); return; }
    if (!this.local.has(key)) this.base.set(key, new Map(this.server.get(key) || []));
    this.local.set(key, value);
    this.version.set(key, (this.version.get(key) || 0) + 1);
    this.save();
    this.scheduleFlush();
  }

  removeItem(key: string): void {
    key = String(key);
    if (DEVICE_KEYS.has(key)) { this.o.persist.removeItem(this.deviceKey(key)); return; }
    if (this.getItem(key) === null) return;
    if (!this.canWrite(key)) { this.refuse(); return; }
    if (!this.local.has(key)) this.base.set(key, new Map(this.server.get(key) || []));
    this.local.set(key, null);
    this.version.set(key, (this.version.get(key) || 0) + 1);
    this.save();
    this.scheduleFlush();
  }

  clear(): void { for (const k of this.keys()) this.removeItem(k); }

  private keys(): string[] {
    const set = new Set<string>();
    for (const [k, rows] of this.server) if (rows.size) set.add(k);
    for (const [k, v] of this.local) { if (v === null) set.delete(k); else set.add(k); }
    for (const k of DEVICE_KEYS) if (this.o.persist.getItem(this.deviceKey(k)) !== null) set.add(k);
    return [...set];
  }
  get length(): number { return this.keys().length; }
  key(i: number): string | null { return this.keys()[i] ?? null; }

  // ------------------------------------------------------------------
  // Permissions
  // ------------------------------------------------------------------
  private canWrite(key: string): boolean { return this.role !== 'viewer' || parseKey(key).kind === 'user'; }
  private refuse() {
    this.o.onNotice?.('You have view-only access, so changes are not saved.');
    queueMicrotask(() => this.o.onExternalChange?.());
  }

  // ------------------------------------------------------------------
  // Sync to the server
  // ------------------------------------------------------------------
  get pending(): number { return this.local.size; }
  private setStatus(s: SyncStatus) { this.status = s; this.o.onStatus?.(s, this.pending); }
  getStatus() { return this.status; }

  private scheduleFlush(ms = this.o.debounceMs ?? 400) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, ms);
    if (this.status !== 'offline') this.setStatus('saving');
  }

  /** Send everything pending. Resolves when done (or when it gave up for now). */
  flush(): Promise<void> {
    if (this.flushing) return this.flushing.then(() => (this.local.size ? this.flush() : undefined));
    if (!this.local.size) { this.setStatus('saved'); return Promise.resolve(); }
    this.flushing = this.doFlush().finally(() => { this.flushing = null; });
    return this.flushing;
  }

  private buildChange(key: string): { change: Change | null; target: RowMap | null } {
    const spec = parseKey(key);
    const table = tableOf(spec)!;
    const value = this.local.get(key)!;
    let target = toRows(table, value);
    if (target === null) return { change: null, target: null }; // not a list (shouldn't happen)
    const base = this.base.get(key) || new Map();
    const server = this.server.get(key) || new Map();
    const upserts: Change['upserts'] = [];
    const deletes: string[] = [];
    for (const [id, row] of target) {
      if (!sameRow(row, base.get(id)) && !sameRow(row, server.get(id))) upserts.push({ id, position: row.position, data: row.data });
    }
    for (const id of base.keys()) if (!target.has(id) && server.has(id)) deletes.push(id);
    // Merge result = server rows, with our changes applied on top.
    const merged: RowMap = new Map(server);
    for (const u of upserts) merged.set(u.id, { position: u.position, data: u.data });
    for (const d of deletes) merged.delete(d);
    target = merged;
    const change: Change = {
      table,
      patientId: spec.kind === 'patient' ? spec.patientId : undefined,
      upserts: isSettingTable(table) ? upserts.map(u => ({ ...u, id: (spec as { key: string }).key })) : upserts,
      deletes: isSettingTable(table) ? deletes.map(() => (spec as { key: string }).key) : deletes,
    };
    return { change, target };
  }

  private async doFlush(): Promise<void> {
    const keys = [...this.local.keys()];
    // Patients first (new people must exist before their medicines), patient deletions last.
    const order = (k: string) => (k === PEOPLE_KEY ? 0 : 1);
    keys.sort((a, b) => order(a) - order(b));
    const knownPatients = () => new Set([...(this.server.get(PEOPLE_KEY)?.keys() || [])]);
    let networkFailed = false;
    let deferredPeopleDeletes: Change | null = null;

    for (const key of keys) {
      if (!this.local.has(key)) continue;
      const ver = this.version.get(key);
      const spec = parseKey(key);
      if (spec.kind === 'patient' && !knownPatients().has(spec.patientId)) {
        // Records for a person who doesn't exist (yet). If removed, just forget it.
        if (this.local.get(key) === null) this.settle(key, ver, this.server.get(key) || new Map());
        continue;
      }
      const { change, target } = this.buildChange(key);
      if (!change || !target) { this.settle(key, ver, this.server.get(key) || new Map()); continue; }
      let sendNow = change;
      if (key === PEOPLE_KEY && change.deletes.length) {
        deferredPeopleDeletes = { ...change, upserts: [] };
        sendNow = { ...change, deletes: [] };
      }
      try {
        if (sendNow.upserts.length || sendNow.deletes.length) await this.o.backend.applyChange(this.o.familyId, sendNow);
        if (key === PEOPLE_KEY && deferredPeopleDeletes) {
          // Apply upserts now so patient checks above see new people; deletions handled after the loop.
          const partial = new Map(this.server.get(PEOPLE_KEY) || []);
          for (const u of sendNow.upserts) partial.set(u.id, { position: u.position, data: u.data });
          this.server.set(PEOPLE_KEY, partial);
        } else {
          this.settle(key, ver, target);
        }
      } catch (e) {
        if (e instanceof RejectedError) {
          this.drop(key);
          this.o.onNotice?.(e.code === '42501' || /permission|policy/i.test(e.message)
            ? 'You don’t have permission to change that, so it was not saved.'
            : `A change could not be saved (${e.message}).`);
          queueMicrotask(() => this.o.onExternalChange?.());
        } else {
          networkFailed = true;
          break;
        }
      }
    }

    if (!networkFailed && deferredPeopleDeletes) {
      const ver = this.version.get(PEOPLE_KEY);
      try {
        await this.o.backend.applyChange(this.o.familyId, deferredPeopleDeletes);
        const { target } = this.buildChange(PEOPLE_KEY);
        this.settle(PEOPLE_KEY, ver, target || new Map());
        // Deleting a person deletes their records on the server too.
        for (const id of deferredPeopleDeletes.deletes) {
          for (const k of [...this.server.keys()]) { const s = parseKey(k); if (s.kind === 'patient' && s.patientId === id) this.server.delete(k); }
        }
      } catch (e) {
        if (e instanceof RejectedError) {
          this.drop(PEOPLE_KEY);
          this.o.onNotice?.('You don’t have permission to remove that person.');
          queueMicrotask(() => this.o.onExternalChange?.());
        } else networkFailed = true;
      }
    }

    this.save();
    if (networkFailed) {
      this.setStatus('offline');
      const wait = this.retryMs;
      this.retryMs = Math.min(this.retryMs * 2, 60000);
      this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, wait);
      return;
    }
    this.retryMs = 2000;
    const touched = keys.some(k => this.remoteTouched.has(k) && !this.local.has(k));
    for (const k of keys) if (!this.local.has(k)) this.remoteTouched.delete(k);
    const orphansOnly = [...this.local.keys()].every(k => { const s = parseKey(k); return s.kind === 'patient' && !knownPatients().has(s.patientId); });
    this.setStatus(this.local.size && !orphansOnly ? 'saving' : 'saved');
    if (touched) queueMicrotask(() => this.o.onExternalChange?.());
  }

  /** Our change reached the server. */
  private settle(key: string, ver: number | undefined, newServerRows: RowMap) {
    this.server.set(key, newServerRows);
    if (this.version.get(key) === ver) { this.local.delete(key); this.base.delete(key); }
    else this.base.set(key, new Map(newServerRows)); // edited again meanwhile: keep the newer edit
  }
  /** The server refused a change: forget it and show the server's version again. */
  private drop(key: string) { this.local.delete(key); this.base.delete(key); }

  // ------------------------------------------------------------------
  // Changes from other devices
  // ------------------------------------------------------------------
  /** Returns true if the screens should re-render. */
  applyRemote(ev: RemoteEvent): boolean {
    if (ev.kind !== 'row') return false;
    const table = ev.type === 'upsert' ? ev.row.table : ev.table;
    const patientId = ev.type === 'upsert' ? ev.row.patientId : ev.patientId;
    const id = ev.type === 'upsert' ? ev.row.id : ev.id;
    const key = isSettingTable(table) ? id : keyFor(table, patientId);
    const rowId = isSettingTable(table) ? 'value' : id;
    const rows = new Map(this.server.get(key) || []);
    if (ev.type === 'upsert') {
      const val = { position: ev.row.position, data: ev.row.data };
      if (sameRow(rows.get(rowId), val)) return false; // our own change echoing back
      rows.set(rowId, val);
    } else {
      if (!rows.has(rowId)) return false;
      rows.delete(rowId);
      if (table === 'patients') {
        for (const k of [...this.server.keys()]) { const s = parseKey(k); if (s.kind === 'patient' && s.patientId === id) this.server.delete(k); }
      }
    }
    this.server.set(key, rows);
    this.save();
    if (this.local.has(key)) { this.remoteTouched.add(key); return false; }
    // Your own date range / selected person changed on another device: used next time
    // the app opens, rather than switching the screen under you now.
    if (table === 'user_settings') return false;
    return true;
  }

  // ------------------------------------------------------------------
  // Helpers used by the app shell
  // ------------------------------------------------------------------
  /** Wait for pending saves (max ~15 s), then reload the page. Used after a full backup restore. */
  async reloadAfterFlush(): Promise<void> {
    await Promise.race([this.flushUntilEmpty(), new Promise(r => setTimeout(r, 15000))]);
    location.reload();
  }
  private async flushUntilEmpty() {
    for (let i = 0; i < 5 && this.local.size; i++) await this.flush();
  }

  /** Every app key and value — same shape as the original backup's `all_storage`. */
  dumpAll(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const k of this.keys()) { const v = this.getItem(k); if (v !== null && isAppKey(k)) out[k] = v; }
    return out;
  }

  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private save() {
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => { this.saveTimer = null; this.saveNow(); }, 50);
  }
  saveNow() {
    const p: Persisted = {
      v: 1,
      server: [...this.server].map(([k, rows]) => [k, [...rows]]),
      local: [...this.local],
      base: [...this.base].map(([k, rows]) => [k, [...rows]]),
      fetchedAt: this.fetchedAt,
    };
    try { this.o.persist.setItem(this.cacheKey, JSON.stringify(p)); }
    catch (e) { console.warn('[Bhorosha] could not save offline copy', e); }
  }

  /** Remove this device's offline copy (on sign out). */
  static clearDeviceCache(persist: PersistStorage & { length: number; key(i: number): string | null }) {
    const del: string[] = [];
    for (let i = 0; i < persist.length; i++) { const k = persist.key(i); if (k && (k.startsWith('bh.cache.') || k.startsWith('bh.dev.'))) del.push(k); }
    del.forEach(k => persist.removeItem(k));
  }
}
